import {
  ref,
  readonly,
  computed,
  useContext
} from "@nuxtjs/composition-api";
import { useCustomerStore } from "../../stores/customer";
import { useCart } from "../useCart";
import { generateUserData } from "../../helpers/generateUserData";
import { useUiNotification } from "@wemade-vsf/composables";
import { Logger, mask } from "@wemade-vsf/core";
import { useEvent } from "@wemade-vsf/composables";
function useUser() {
  const customerStore = useCustomerStore();
  const { app, $recaptcha } = useContext();
  const { setCart } = useCart();
  const { send: sendNotification } = useUiNotification();
  const loading = ref(false);
  const errorsFactory = () => ({
    updateUser: null,
    register: null,
    login: null,
    logout: null,
    changePassword: null,
    load: null
  });
  const error = ref(errorsFactory());
  const setUser = (newUser) => {
    customerStore.user = newUser;
    Logger.debug("useUserFactory.setUser", newUser);
  };
  const resetErrorValue = () => {
    error.value = errorsFactory();
  };
  const updateCustomerEmail = async (credentials) => {
    const { errors } = await app.context.$vsf.$magento.api.updateCustomerEmail(credentials);
    if (errors) {
      throw errors.map((e) => e.message).join(",");
    }
  };
  const updateUser = async ({ user: providedUser, customQuery, customHeaders }) => {
    var _a;
    Logger.debug("[Magento] Update user information", { providedUser, customQuery, customHeaders });
    resetErrorValue();
    try {
      loading.value = true;
      const { email: oldEmail } = customerStore.user;
      const { email, password, ...updateData } = providedUser;
      const userData = generateUserData(updateData);
      if (email && email !== oldEmail) {
        await updateCustomerEmail({
          email,
          password
        });
      }
      const { data, errors } = await app.context.$vsf.$magento.api.updateCustomer(userData, customQuery, customHeaders);
      Logger.debug("[Result]:", { data });
      if (errors) {
        const allErrorMessages = errors.map((e) => e.message).join(",");
        Logger.error(allErrorMessages);
        error.value.updateUser = allErrorMessages;
      }
      customerStore.user = ((_a = data == null ? void 0 : data.updateCustomerV2) == null ? void 0 : _a.customer) || {};
      error.value.updateUser = null;
    } catch (err) {
      error.value.updateUser = err;
      Logger.error("useUser/updateUser", err);
    } finally {
      loading.value = false;
    }
  };
  const logout = async ({ customQuery = {}, customHeaders = {} } = {}) => {
    Logger.debug("[Magento] useUserFactory.logout");
    resetErrorValue();
    try {
      const apiState = app.context.$vsf.$magento.config.state;
      await app.context.$vsf.$magento.api.revokeCustomerToken(customQuery, customHeaders);
      apiState.removeCustomerToken();
      apiState.removeCartId();
      apiState.removeCacheId();
      setCart(null);
      customerStore.setIsLoggedIn(false);
      error.value.logout = null;
      customerStore.user = null;
      const allCookies = app.$cookies.getAll();
      Object.keys(allCookies).forEach((key) => {
        if (key.startsWith("vsf") && !key.includes("store") && !key.includes("currency")) {
          app.$cookies.remove(key);
        }
      });
      useEvent("user:logout");
    } catch (err) {
      error.value.logout = err;
      Logger.error("useUser/logout", err);
    }
  };
  const load = async ({ customQuery = {}, customHeaders = {} } = {}) => {
    var _a, _b;
    Logger.debug("[Magento] useUser.load");
    resetErrorValue();
    try {
      loading.value = true;
      const apiState = app.context.$vsf.$magento.config.state;
      if (!apiState.getCustomerToken()) {
        return null;
      }
      try {
        const { data } = await app.context.$vsf.$magento.api.customer(customQuery, customHeaders);
        Logger.debug("[Result]:", { data });
        customerStore.user = (_a = data == null ? void 0 : data.customer) != null ? _a : {};
        if ((_b = data["headers"]) == null ? void 0 : _b["cacheId"]) {
          apiState.setCacheId(data["headers"]["cacheId"]);
        }
      } catch {
        await logout();
      }
      error.value.load = null;
    } catch (err) {
      error.value.load = err;
      Logger.error("useUser/load", err);
    } finally {
      loading.value = false;
    }
    return customerStore.user;
  };
  const login = async ({ user: providedUser, customQuery, customHeaders }) => {
    Logger.debug("[Magento] useUser.login", providedUser);
    resetErrorValue();
    try {
      loading.value = true;
      const apiState = app.context.$vsf.$magento.config.state;
      const result = await app.$vsf.$magento.api.generateCustomerToken(
        {
          email: providedUser.email,
          password: providedUser.password,
          recaptchaToken: providedUser.recaptchaToken
        },
        customQuery || {},
        customHeaders || {}
      );
      Logger.debug("[Result]:", result);
      const { data, errors } = result;
      if (errors) {
        const joinedErrors = errors.map((e) => e.message).join(",");
        Logger.error(joinedErrors);
        errors.forEach((registerError, i) => sendNotification({
          icon: "error",
          id: Symbol(`registration_error-${i}`),
          message: registerError.message,
          persist: true,
          title: "Registration error",
          type: "danger"
        }));
        throw new Error(joinedErrors);
      }
      customerStore.setIsLoggedIn(true);
      apiState.setCustomerToken(data.generateCustomerToken.token);
      useEvent("user:login");
      const currentCartId = apiState.getCartId();
      const cart = await app.context.$vsf.$magento.api.customerCart();
      const newCartId = cart.data.customerCart.id;
      try {
        if (newCartId && currentCartId && currentCartId !== newCartId) {
          const { data: dataMergeCart } = await app.context.$vsf.$magento.api.mergeCarts(
            {
              sourceCartId: currentCartId,
              destinationCartId: newCartId
            }
          );
          setCart(dataMergeCart.mergeCarts);
          apiState.setCartId(dataMergeCart.mergeCarts.id);
        } else {
          setCart(cart.data.customerCart);
        }
      } catch {
        setCart(cart.data.customerCart);
      }
      load();
      error.value.login = null;
    } catch (err) {
      error.value.login = err;
      Logger.error("useUser/login", err);
    } finally {
      loading.value = false;
    }
  };
  const register = async ({ user: providedUser, customQuery, customHeaders }) => {
    Logger.debug("[Magento] useUser.register", providedUser);
    resetErrorValue();
    try {
      loading.value = true;
      const {
        email,
        password,
        recaptchaToken,
        ...baseData
      } = generateUserData(providedUser);
      const { data, errors } = await app.$vsf.$magento.api.createCustomer(
        {
          email,
          password,
          recaptchaToken,
          ...baseData
        },
        customQuery || {},
        customHeaders || {}
      );
      Logger.debug("[Result]:", { data });
      if (errors) {
        const joinedErrors = errors.map((e) => e.message).join(",");
        Logger.error(joinedErrors);
        errors.forEach((registerError, i) => sendNotification({
          icon: "error",
          id: Symbol(`registration_error-${i}`),
          message: registerError.message,
          persist: true,
          title: "Registration error",
          type: "danger"
        }));
        throw new Error(joinedErrors);
      }
      error.value.register = null;
      let loginRecaptchaToken = "";
      if ($recaptcha && recaptchaToken) {
        loginRecaptchaToken = await $recaptcha.getResponse();
      }
      const { customer: { customer_create_account_confirm } } = app.context.$vsf.$magento.config;
      if (customer_create_account_confirm) {
        return await new Promise((resolve) => {
          sendNotification({
            id: Symbol("registration_confirmation"),
            message: app.i18n.t("You must confirm your account. Please check your email for the confirmation link."),
            persist: true,
            title: "Registration confirmation",
            type: "success",
            icon: "check"
          });
          resolve();
        });
      }
      useEvent("user:register");
      await login({ user: { email, password, recaptchaToken: loginRecaptchaToken }, customQuery: {} });
    } catch (err) {
      error.value.register = err;
      Logger.error("useUser/register", err);
    } finally {
      loading.value = false;
    }
  };
  const changePassword = async (params) => {
    Logger.debug("[Magento] useUser.changePassword", { currentPassword: mask(params.current), newPassword: mask(params.new) });
    resetErrorValue();
    try {
      loading.value = true;
      const { data, errors } = await app.context.$vsf.$magento.api.changeCustomerPassword({
        currentUser: customerStore.user,
        currentPassword: params.current,
        newPassword: params.new
      }, params.customQuery, params == null ? void 0 : params.customHeaders);
      let joinedErrors = null;
      if (errors) {
        joinedErrors = errors.map((e) => e.message).join(",");
        Logger.error(joinedErrors);
      }
      Logger.debug("[Result] ", { data });
      customerStore.user = data == null ? void 0 : data.changeCustomerPassword;
      error.value.changePassword = joinedErrors;
    } catch (err) {
      error.value.changePassword = err;
      Logger.error("useUser/changePassword", err);
    } finally {
      loading.value = false;
    }
  };
  return {
    setUser,
    updateUser,
    register,
    login,
    logout,
    changePassword,
    load,
    loading: readonly(loading),
    error: readonly(error),
    user: computed(() => customerStore.user),
    isCompanyCustomer: computed(() => customerStore.isCompanyCustomer),
    isAuthenticated: computed(() => customerStore.isLoggedIn)
  };
}
var useUser_default = useUser;
export * from "./useUser";
export {
  useUser_default as default,
  useUser
};
